/**
 * Copyright © 2026 Delicious AI, LLC
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { Alert, Box, Stack, Tooltip, Typography } from '@mui/material'

import useT from '@/i18n/useT'

type Props = {
  changedAt?: string | null
  completedAt?: string | null
  completedBy?: string | null
  compact?: boolean
}

export default function PlanogramRestockStatus({
  changedAt,
  completedAt,
  completedBy,
  compact = false,
}: Props) {
  const { t } = useT()
  if (!changedAt) return null

  const completed = Boolean(completedAt)
  const label = completed
    ? t(
        'planogramRestockStatus.completedTooltip',
        'Restock completed for latest planogram version',
      )
    : t(
        'planogramRestockStatus.pendingTooltip',
        'Restock pending for latest planogram version',
      )
  const icon = completed ? <CheckCircleIcon /> : <ScheduleIcon />

  if (compact) {
    return (
      <Tooltip title={label} arrow>
        <Box
          aria-label={label}
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: completed ? 'success.main' : 'warning.main',
            color: completed ? 'success.contrastText' : 'warning.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 1,
            '& svg': { fontSize: 17 },
          }}
        >
          {icon}
        </Box>
      </Tooltip>
    )
  }

  return (
    <Alert
      severity={completed ? 'success' : 'warning'}
      icon={icon}
      sx={{ py: 0.5, alignItems: 'center' }}
    >
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={600}>
          {completed
            ? t(
                'planogramRestockStatus.completed',
                'Planogram restock completed',
              )
            : t('planogramRestockStatus.pending', 'Planogram restock pending')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {completedAt
            ? completedBy
              ? t(
                  'planogramRestockStatus.completedAtBy',
                  'Completed {completedAt} by {completedBy}',
                  {
                    completedAt: new Date(completedAt).toLocaleString(),
                    completedBy,
                  },
                )
              : t(
                  'planogramRestockStatus.completedAt',
                  'Completed {completedAt}',
                  {
                    completedAt: new Date(completedAt).toLocaleString(),
                  },
                )
            : t('planogramRestockStatus.changedAt', 'Changed {changedAt}', {
                changedAt: new Date(changedAt).toLocaleString(),
              })}
        </Typography>
      </Stack>
    </Alert>
  )
}
